import React, {useState, forwardRef, useImperativeHandle} from 'react';
import {
    Box,
    Typography,
    Stack,
    Chip,
    Grid,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Switch,
    FormControlLabel,
    FormGroup,
    CircularProgress,
    Snackbar,
    Divider,
    Avatar,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import {
    AdminPanelSettings as AdminIcon,
    School as TeacherIcon,
    Person as StudentIcon,
    Block as BlockIcon,
    CheckCircle as ActiveIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    PersonOutline as PersonIcon,
    Email as EmailIcon,
    CalendarToday as CalendarIcon,
    Security as SecurityIcon,
    Verified as VerifiedIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';

const ProfileDetails = forwardRef(({selectedUser, onUserUpdate, apiClient}, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editData, setEditData] = useState({});
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});

    // Initialize edit data when entering edit mode
    const handleStartEdit = () => {
        setEditData({
            username: selectedUser?.username || '',
            is_suspended: selectedUser?.is_suspended || false,
            soft_deleted: selectedUser?.soft_deleted || false,
            roles: selectedUser?.roles || []
        });
        setIsEditing(true);
    };

    // Expose save function through ref
    useImperativeHandle(ref, () => ({
        save: handleSave
    }));

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle role changes with mutual exclusivity validation
    const handleRoleChange = (role, checked) => {
        setEditData(prev => {
            let newRoles = [...(prev.roles || [])];

            if (checked) {
                // Add the role
                if (!newRoles.includes(role)) {
                    newRoles.push(role);
                }

                // Enforce mutual exclusivity between student and teacher
                if (role === 'student' && newRoles.includes('teacher')) {
                    newRoles = newRoles.filter(r => r !== 'teacher');
                    setSnackbar({
                        open: true,
                        message: 'Teacher role removed - users cannot be both student and teacher',
                        severity: 'info'
                    });
                } else if (role === 'teacher' && newRoles.includes('student')) {
                    newRoles = newRoles.filter(r => r !== 'student');
                    setSnackbar({
                        open: true,
                        message: 'Student role removed - users cannot be both student and teacher',
                        severity: 'info'
                    });
                }
            } else {
                // Remove the role
                newRoles = newRoles.filter(r => r !== role);
            }

            return {
                ...prev,
                roles: newRoles
            };
        });
    };

    // Save changes - separate profile and role updates
    const handleSave = async () => {
        if (!apiClient || !selectedUser) {
            console.error('Missing apiClient or selectedUser');
            return;
        }

        console.log('Starting save process...');
        console.log('Selected user:', selectedUser);
        console.log('Edit data:', editData);

        setIsLoading(true);
        try {
            // Prepare profile update data (excluding roles)
            const profileUpdateData = {
                id: selectedUser.id,
                username: editData.username,
                is_suspended: editData.is_suspended,
                soft_deleted: editData.soft_deleted
            };

            // Update profile information first
            console.log('Making PUT request to:', `/api/users/${selectedUser.id}`);
            console.log('Profile update data:', profileUpdateData);

            const profileResponse = await apiClient.put(`/api/users/${selectedUser.id}`, profileUpdateData);
            console.log('Profile update response:', profileResponse);

            let finalUserData = profileResponse.data || profileResponse;

            // Update roles separately using the dedicated endpoint
            const currentRoles = selectedUser.roles?.sort() || [];
            const newRoles = (editData.roles || []).sort();
            const rolesChanged = JSON.stringify(currentRoles) !== JSON.stringify(newRoles);

            console.log('Current roles:', currentRoles);
            console.log('New roles:', newRoles);
            console.log('Roles changed:', rolesChanged);

            if (rolesChanged) {
                console.log('Making PUT request to:', `/api/users/${selectedUser.id}/roles`);
                console.log('Roles update data:', {roles: editData.roles});

                const rolesResponse = await apiClient.put(`/api/users/${selectedUser.id}/roles`, {
                    roles: editData.roles
                });
                console.log('Roles update response:', rolesResponse);

                // Get the updated user data after role change to ensure we have latest data
                console.log('Fetching updated user data...');
                const updatedUserResponse = await apiClient.get(`/api/users/${selectedUser.id}`);
                console.log('Updated user response:', updatedUserResponse);

                finalUserData = updatedUserResponse.data || updatedUserResponse;
            }

            console.log('Final user data:', finalUserData);

            // Notify parent component of the update
            if (onUserUpdate) {
                console.log('Calling onUserUpdate with:', finalUserData);
                onUserUpdate(finalUserData);
            }

            setSnackbar({open: true, message: 'User updated successfully!', severity: 'success'});
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update user - Full error:', error);
            console.error('Error response:', error.response);

            // Extract error message from response
            let errorMessage = 'Failed to update user';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            console.error('Extracted error message:', errorMessage);

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Cancel editing
    const handleCancel = () => {
        setEditData({});
        setIsEditing(false);
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({...prev, open: false}));
    };

    // Helper function to check if user has a specific role
    const userHasRole = (user, role) => {
        return user.roles && user.roles.includes(role);
    };

    // Enhanced function to get all user roles using the roles array
    const getUserRoles = (user) => {
        const roles = [];
        const userRoles = user.roles || [];

        if (userRoles.includes('admin')) {
            roles.push({label: 'Admin', color: 'error', icon: AdminIcon});
        }
        if (userRoles.includes('teacher')) {
            roles.push({label: 'Teacher', color: 'primary', icon: TeacherIcon});
        }
        if (userRoles.includes('student')) {
            roles.push({label: 'Student', color: 'secondary', icon: StudentIcon});
        }

        // If no roles specified at all, default to student
        if (userRoles.length === 0) {
            roles.push({label: 'Student', color: 'secondary', icon: StudentIcon});
        }

        return roles;
    };

    // Get user status display
    const getUserStatus = (user) => {
        if (user.soft_deleted) return {label: 'Deleted', color: 'error', icon: BlockIcon};
        if (user.is_suspended) return {label: 'Suspended', color: 'warning', icon: BlockIcon};
        return {label: 'Active', color: 'success', icon: ActiveIcon};
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    // Component for displaying multiple roles
    const RoleCell = ({user}) => {
        const roles = getUserRoles(user);

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {roles.map((role, index) => {
                    const RoleIcon = role.icon;
                    return (
                        <Chip
                            key={index}
                            label={role.label}
                            color={role.color}
                            size="small"
                            icon={<RoleIcon/>}
                        />
                    );
                })}
            </Stack>
        );
    };

    // Validation helper for role selection - Fixed to handle undefined roles
    const isRoleSelectionValid = (roles) => {
        if (!roles || !Array.isArray(roles)) return true;
        return !(roles.includes('student') && roles.includes('teacher'));
    };

    if (!selectedUser) {
        return (
            <Box sx={{p: 2, textAlign: 'center'}}>
                <Avatar sx={{width: 64, height: 64, mx: 'auto', mb: 2}}>
                    <PersonIcon sx={{fontSize: 32}}/>
                </Avatar>
                <Typography variant="h6" gutterBottom>
                    No user selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Select a user from the list to view their profile details
                </Typography>
            </Box>
        );
    }

    const availableRoles = ['admin', 'teacher', 'student'];
    const userStatus = getUserStatus(selectedUser);

    return (
        <Box sx={{p: 2}}>
            {/* Header Section - Just Edit Button */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2}}>
                {!isEditing ? (
                    <Tooltip title="Edit Profile">
                        <IconButton onClick={handleStartEdit} color="primary">
                            <EditIcon/>
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon/>}
                            onClick={handleCancel}
                            size="small"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={16} color="inherit"/> : <SaveIcon/>}
                            onClick={handleSave}
                            size="small"
                            disabled={isLoading || !isRoleSelectionValid(editData.roles)}
                        >
                            Save
                        </Button>
                    </Stack>
                )}
            </Box>

            {/* Main Content Area - Stacked Vertically */}
            <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid size={{xs: 12}}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar size="small">
                                <PersonIcon/>
                            </Avatar>
                            <Typography variant="h6">
                                Basic Information
                            </Typography>
                        </Stack>

                        <Grid container spacing={3}>
                            <Grid size={{xs: 12, md: 4}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <PersonIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Username
                                        </Typography>
                                    </Stack>
                                    {isEditing ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={editData.username}
                                            onChange={(e) => handleFieldChange('username', e.target.value)}
                                        />
                                    ) : (
                                        <Typography variant="body1">
                                            {selectedUser.username || 'N/A'}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 4}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <EmailIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Email Address
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body1">
                                        {selectedUser.email || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 4}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <SecurityIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            User Roles
                                        </Typography>
                                    </Stack>
                                    {isEditing ? (
                                        <Box>
                                            <Paper variant="outlined" sx={{p: 2}}>
                                                <FormGroup>
                                                    {availableRoles.map((role) => (
                                                        <FormControlLabel
                                                            key={role}
                                                            control={
                                                                <Switch
                                                                    checked={(editData.roles || []).includes(role)}
                                                                    onChange={(e) => handleRoleChange(role, e.target.checked)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={role.charAt(0).toUpperCase() + role.slice(1)}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Paper>
                                        </Box>
                                    ) : (
                                        <RoleCell user={selectedUser}/>
                                    )}
                                </Box>
                            </Grid>

                            {/* Role validation alert in full width if needed */}
                            {isEditing && (editData.roles || []).includes('student') && (editData.roles || []).includes('teacher') && (
                                <Grid size={{xs: 12}}>
                                    <Alert severity="warning">
                                        Users cannot be both student and teacher. Please select only one.
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Account Status & Metadata */}
                <Grid size={{xs: 12}}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar size="small">
                                <SecurityIcon/>
                            </Avatar>
                            <Typography variant="h6">
                                Account Status
                            </Typography>
                        </Stack>

                        <Grid container spacing={3}>
                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Current Status
                                    </Typography>
                                    {isEditing ? (
                                        <Paper variant="outlined" sx={{p: 2}}>
                                            <Stack spacing={1}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={editData.is_suspended}
                                                            onChange={(e) => handleFieldChange('is_suspended', e.target.checked)}
                                                            color="warning"
                                                        />
                                                    }
                                                    label="Account Suspended"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={editData.soft_deleted}
                                                            onChange={(e) => handleFieldChange('soft_deleted', e.target.checked)}
                                                            color="error"
                                                        />
                                                    }
                                                    label="Soft Deleted"
                                                />
                                            </Stack>
                                        </Paper>
                                    ) : (
                                        <Chip
                                            label={userStatus.label}
                                            color={userStatus.color}
                                            icon={<userStatus.icon/>}
                                            variant={userStatus.label === 'Active' ? 'outlined' : 'filled'}
                                        />
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <VerifiedIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Onboarding Status
                                        </Typography>
                                    </Stack>
                                    <Chip
                                        label={selectedUser.onboarding_complete ? 'Complete' : 'Pending'}
                                        color={selectedUser.onboarding_complete ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <Divider sx={{my: 1}}/>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <CalendarIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Account Created
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2">
                                        {formatTimestamp(selectedUser.created_at)}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                        <ScheduleIcon fontSize="small" color="action"/>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Last Updated
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2">
                                        {formatTimestamp(selectedUser.updated_at)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Soft Delete Alert */}
                {selectedUser.soft_deleted_at && (
                    <Grid size={{xs: 12}}>
                        <Alert severity="warning">
                            This user was soft deleted on {formatTimestamp(selectedUser.soft_deleted_at)}
                        </Alert>
                    </Grid>
                )}
            </Grid>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{width: '100%'}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
});

ProfileDetails.displayName = 'ProfileDetails';

export default ProfileDetails;